import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ChatChannelTypeEnum, CONSTANTS, Constants } from 'lib-trend-core';
import { ChatActions, bootstrapLoadedSelector, buildChatAppPath, conversationsSelector } from 'lib-trend-core/chat';
import { Inject } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

/**
 * `/dm/:userId` — the direct conversation with a person (T-31 · RF-02). An existing one
 * replaces the address with its own `/c/:id`; otherwise a draft opens, and the address
 * moves to the real conversation once the first message creates it.
 */
@Component({
  selector: 'chat-app-direct-route',
  template: `
    @if (userId) {
      <chat-conversation
        class="d-block flex-grow-1 chat-app__conversation"
        [channelId]="null"
        [directUserId]="userId"
        [showBack]="true"
        (back)="backToList()"
      ></chat-conversation>
    } @else {
      <chat-message-list-skeleton class="flex-grow-1"></chat-message-list-skeleton>
    }
  `,
  host: { class: 'd-flex flex-column flex-grow-1 overflow-hidden' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAppDirectRouteComponent implements OnInit, OnDestroy {

  userId: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    @Inject(CONSTANTS) private readonly constants: Constants,
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.route.paramMap.pipe(map((params) => params.get('userId'))),
      this.store.select(bootstrapLoadedSelector).pipe(filter(Boolean), take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userId]) => this.resolve(userId));
  }

  private resolve(userId: string | null): void {
    if (!userId) {
      this.router.navigateByUrl('/', { replaceUrl: true });
      return;
    }
    const currentUserId = this.currentUserId();
    const directKey = [currentUserId, userId].sort().join(':');

    this.store.select(conversationsSelector).pipe(take(1)).subscribe((conversations) => {
      const existing = (conversations ?? []).find((conversation) =>
        conversation.type === ChatChannelTypeEnum.DIRECT && conversation.directKey === directKey);
      if (existing) {
        this.router.navigateByUrl(buildChatAppPath({ channelId: existing._id }), { replaceUrl: true });
        return;
      }
      this.store.dispatch(ChatActions.openDirectDraft({ userId, directKey }));
      this.userId = userId;
    });
  }

  backToList(): void {
    this.router.navigateByUrl('/');
  }

  private currentUserId(): string {
    try {
      const raw = localStorage.getItem(this.constants['currentUser']);
      return String(raw ? JSON.parse(raw)?.user?._id ?? '' : '');
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
